import { getParsingStatus, ParsingStatusHotelItem, ParsingStatusResponse, ParsingStatusTask } from "../api/parsing";
import type { AdminHotelRow, HotelGuests, HotelGuestsChild, HotelMinStay, HotelOut, ParsingTask } from "../types/hotels";

type HotelLike = Pick<HotelOut | AdminHotelRow, "id" | "group_id" | "min_stay" | "guests" | "guests_child" | "parsing">;

export type HotelDisplaySettings = {
  min_stay: HotelMinStay;
  guests: HotelGuests;
  guests_child: HotelGuestsChild;
};

type ParsedParsingSettings = {
  minStay: number[];
  guests: number[];
  guestsChild: number[];
};

const CHILD_GUEST_COUNTS = [1, 2, 3];

function toNumber(value: unknown) {
  const numberValue = typeof value === "string" ? Number(value) : value;
  return typeof numberValue === "number" && Number.isFinite(numberValue) ? numberValue : null;
}

function toPositiveInt(value: unknown) {
  const numberValue = toNumber(value);
  return numberValue !== null && Number.isInteger(numberValue) && numberValue > 0 ? numberValue : null;
}

function uniqueSorted(values: Array<number | null | undefined>) {
  return Array.from(
    new Set(
      values.filter((value): value is number => value != null && Number.isInteger(value) && value > 0),
    ),
  ).sort((left, right) => left - right);
}

function normalizeArray(values?: number[] | null) {
  return uniqueSorted(values ?? []);
}

function normalizeChildGuestCounts(value: unknown) {
  if (Array.isArray(value)) {
    const numericValues = value.map(toPositiveInt).filter((item): item is number => item !== null);
    if (!numericValues.length) return [];
    const valuesLookLikeCounts = numericValues.every((item) => CHILD_GUEST_COUNTS.includes(item));
    return valuesLookLikeCounts ? numericValues : [numericValues.length];
  }

  const childCount = toPositiveInt(value);
  return childCount !== null && CHILD_GUEST_COUNTS.includes(childCount) ? [childCount] : [];
}

function getTaskChildGuests(task: ParsingStatusTask | ParsingTask) {
  const apiTask = task as ParsingStatusTask;
  return normalizeChildGuestCounts(apiTask.guests_child ?? apiTask.children);
}

function parseGuestValue(value: unknown) {
  if (typeof value === "string") {
    const childMatch = value.match(/^2\+([1-9]\d*)$/);
    if (childMatch) {
      return {
        guests: 2,
        guestsChild: Number(childMatch[1]),
      };
    }
  }

  return {
    guests: toPositiveInt(value),
    guestsChild: null,
  };
}

function parseTasks(tasks: Array<ParsingStatusTask | ParsingTask | null | undefined>): ParsedParsingSettings {
  const minStay: number[] = [];
  const guests: number[] = [];
  const guestsChild: number[] = [];

  tasks.forEach((task) => {
    if (!task) return;
    const parsedGuests = parseGuestValue((task as ParsingStatusTask).guests);
    const parsedMinStay = toPositiveInt((task as ParsingStatusTask).min_stay);
    if (parsedMinStay !== null) minStay.push(parsedMinStay);
    if (parsedGuests.guests !== null) guests.push(parsedGuests.guests);
    if (parsedGuests.guestsChild !== null) guestsChild.push(parsedGuests.guestsChild);
    guestsChild.push(...getTaskChildGuests(task));
  });

  return {
    minStay: uniqueSorted(minStay),
    guests: uniqueSorted(guests),
    guestsChild: uniqueSorted(guestsChild),
  };
}

function isTaskLike(value: unknown): value is ParsingStatusTask {
  return Boolean(
    value &&
      typeof value === "object" &&
      ("min_stay" in value || "guests" in value) &&
      !("parsing" in value) &&
      !("tasks" in value) &&
      !("parsings" in value),
  );
}

function getItemTasks(item: ParsingStatusHotelItem | ParsingStatusTask) {
  if (isTaskLike(item)) return [item];
  return item.parsing ?? item.tasks ?? item.parsings ?? item.items ?? [];
}

function getResponsePayload(response: ParsingStatusResponse): unknown {
  if (!response || typeof response !== "object" || Array.isArray(response)) return response;
  if ("data" in response && response.data != null) return response.data;
  if ("items" in response && response.items != null) return response.items;
  if ("result" in response && response.result != null) return response.result;
  if ("parsings" in response && response.parsings != null) return response.parsings;
  if ("objects" in response && response.objects != null) return response.objects;
  return response;
}

function addSettings(target: ParsedParsingSettings, settings: ParsedParsingSettings) {
  target.minStay.push(...settings.minStay);
  target.guests.push(...settings.guests);
  target.guestsChild.push(...settings.guestsChild);
}

function normalizeParsedSettings(settings: ParsedParsingSettings): ParsedParsingSettings {
  return {
    minStay: uniqueSorted(settings.minStay),
    guests: uniqueSorted(settings.guests),
    guestsChild: uniqueSorted(settings.guestsChild),
  };
}

function parseStatusResponse(response: ParsingStatusResponse, fallbackGroupIds: number[]) {
  const byGroupId: Record<string, ParsedParsingSettings> = {};
  const addToGroup = (groupId: number | string | null | undefined, settings: ParsedParsingSettings) => {
    if (groupId === null || groupId === undefined || groupId === "") return;
    const key = String(groupId);
    byGroupId[key] = byGroupId[key] ?? { minStay: [], guests: [], guestsChild: [] };
    addSettings(byGroupId[key], settings);
  };

  const payload = getResponsePayload(response);
  if (Array.isArray(payload)) {
    payload.forEach((item) => {
      if (!item) return;
      const groupId = (item as ParsingStatusHotelItem).group_id;
      const tasks = getItemTasks(item as ParsingStatusHotelItem | ParsingStatusTask);
      const settings = parseTasks(tasks);
      addToGroup(groupId, settings);
    });

    if (fallbackGroupIds.length === 1 && Object.keys(byGroupId).length === 0) {
      addToGroup(fallbackGroupIds[0], parseTasks(payload as ParsingStatusTask[]));
    }
  } else if (payload && typeof payload === "object") {
    Object.entries(payload as Record<string, ParsingStatusHotelItem | ParsingStatusTask[] | ParsingStatusTask | null>).forEach(
      ([groupId, value]) => {
        if (!value) return;
        const tasks = Array.isArray(value) ? value : getItemTasks(value);
        addToGroup((value as ParsingStatusHotelItem).group_id ?? groupId, parseTasks(tasks));
      },
    );
  }

  return Object.entries(byGroupId).reduce<Record<string, ParsedParsingSettings>>((acc, [groupId, settings]) => {
    acc[groupId] = normalizeParsedSettings(settings);
    return acc;
  }, {});
}

function combineSettings(hotel: HotelLike, parsingSettings?: ParsedParsingSettings): HotelDisplaySettings {
  const minStay = uniqueSorted([
    ...normalizeArray(hotel.min_stay),
    ...(parsingSettings?.minStay ?? []),
  ]);
  const guests = uniqueSorted([
    ...normalizeArray(hotel.guests),
    ...(parsingSettings?.guests ?? []),
  ]);
  const guestsChild = uniqueSorted([
    ...normalizeArray(hotel.guests_child),
    ...(parsingSettings?.guestsChild ?? []),
  ]);

  return {
    min_stay: minStay.length ? minStay : null,
    guests: guests.length ? guests : null,
    guests_child: guestsChild.length ? guestsChild : null,
  };
}

export async function loadHotelsDisplaySettings(hotels: HotelLike[]) {
  const groupIds = uniqueSorted(hotels.map((hotel) => hotel.group_id ?? null));
  let parsingByGroupId: Record<string, ParsedParsingSettings> = {};

  if (groupIds.length) {
    try {
      parsingByGroupId = parseStatusResponse(await getParsingStatus(), groupIds);
    } catch (error) {
      console.warn("Не удалось загрузить parsing_status, используем настройки объекта", error);
    }
  }

  return hotels.reduce<Record<string, HotelDisplaySettings>>((acc, hotel) => {
    const settingsFromStatus = hotel.group_id !== null ? parsingByGroupId[String(hotel.group_id)] : undefined;
    const fallbackParsingSettings = parseTasks(hotel.parsing ?? []);
    acc[String(hotel.id)] = combineSettings(hotel, settingsFromStatus ?? fallbackParsingSettings);
    return acc;
  }, {});
}

export function getHotelDisplaySettings(
  settingsMap: Record<string, HotelDisplaySettings>,
  hotel: HotelLike | null | undefined,
): HotelDisplaySettings {
  if (!hotel) return { min_stay: null, guests: null, guests_child: null };
  return settingsMap[String(hotel.id)] ?? combineSettings(hotel, parseTasks(hotel.parsing ?? []));
}
